<script setup lang="ts">
/**
 * Spielweise des ausgewählten Takts im Bass-Vorschau-System.
 *
 * Wechselbass (Vorgabe), die Akkordtöne einzeln oder ein Helikon-Basslauf. Anders als von Hand
 * geschriebene Noten ist das eine **Einstellung**: der Takt wird weiterhin neu gebaut und folgt
 * damit auch einer späteren Akkordkorrektur.
 */
import {computed} from 'vue';
import {Icon} from '@iconify/vue';
import {
  BASS_RUN_LABELS,
  BASS_RUN_PREFIX,
  BASS_STYLE_AKKORDTOENE,
  BASS_STYLE_WECHSELBASS,
} from '@/composables/musicXmlDom';
import {injectScoreEditContext} from './editContext';

const {selectedMeasureIndex, bassStyles, setBassStyle} = injectScoreEditContext();

const current = computed(() => {
  const index = selectedMeasureIndex.value;
  if (index === null) return BASS_STYLE_WECHSELBASS;
  return bassStyles.value[index] ?? BASS_STYLE_WECHSELBASS;
});

const options = computed(() => [
  {value: BASS_STYLE_WECHSELBASS, label: 'Wechselbass', hint: 'Grundbass · Akkord · Gegenbass · Akkord'},
  {value: BASS_STYLE_AKKORDTOENE, label: 'Akkordtöne einzeln', hint: 'Grundton, Terz, Quinte nacheinander'},
  ...Object.entries(BASS_RUN_LABELS).map(([name, label]) => ({
    value: `${BASS_RUN_PREFIX}${name}`,
    label,
    hint: 'Basslauf, auf den Akkord des Takts bezogen',
  })),
]);

function choose(style: string) {
  const index = selectedMeasureIndex.value;
  if (index === null || style === current.value) return;
  setBassStyle(index, style);
}
</script>

<template>
  <div class="w-72 p-3">
    <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
      Spielweise dieses Takts
    </p>

    <div class="space-y-1">
      <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="flex w-full items-start gap-2 rounded-lg border p-2 text-left transition-all duration-200"
          :class="current === option.value
            ? 'border-violet-300 bg-violet-50 shadow-sm'
            : 'border-gray-200 bg-white hover:border-violet-200'"
          @click="choose(option.value)"
      >
        <Icon
            :icon="current === option.value ? 'fa6-solid:circle-dot' : 'fa6-regular:circle'"
            class="mt-0.5 shrink-0 text-xs"
            :class="current === option.value ? 'text-violet-600' : 'text-gray-400'"
        />
        <span>
          <span class="block text-sm font-medium text-gray-800">{{ option.label }}</span>
          <span class="block text-xs text-gray-500">{{ option.hint }}</span>
        </span>
      </button>
    </div>

    <p class="mt-3 text-xs text-gray-500">
      Die Wahl bleibt erhalten: der Takt wird weiterhin neu erzeugt und folgt damit auch einer
      späteren Akkordkorrektur.
    </p>
  </div>
</template>
